import * as React from 'react'
import { Flex, Input, Select, Switch, Table, Tag, Tooltip } from 'antd'
import { Button, Well } from '@pushly/aqe/lib/components'
import type { ButtonAction } from '@pushly/aqe/lib/components'
import {
    CaretRightOutlined,
    DeleteOutlined,
    EditOutlined,
    LockOutlined,
    PlusOutlined,
    QuestionCircleOutlined,
} from '@ant-design/icons'
import { CatalogFieldMapping } from '@pushly/models/lib/structs/catalogs/catalog-field-mapping'
import { CatalogItemType } from '@pushly/models/lib/enums/catalogs/catalog-item-type'
import { ItemIdStrategy } from '@pushly/models/lib/enums/catalogs/item-id-strategy'
import titleCase from 'title-case'
import {
    EVENT_SCHEMA_SUBTYPES,
    PRODUCT_SCHEMA_SUBTYPES,
    REQUIRED_CATALOG_FIELDS,
} from '@halo/constants/catalog-default-mappings'
import ItemIdStrategies from './item-id-strategies'
import {
    DISPLAY_STRATEGY_JSONPATH_CUSTOM,
    ILocalCatalogConfig,
    ILocalItemIdConfig,
} from './types'

interface IProps {
    config: ILocalCatalogConfig
    configIndex: number
    availableItemTypes: CatalogItemType[]
    isExpanded: boolean
    onToggleExpanded: () => void
    onUpdateConfig: (patch: Partial<ILocalCatalogConfig>) => void
    onRemoveConfig: () => void
    onChangeItemType: (newItemType: CatalogItemType) => void
    canAddMapping: boolean
    onAddMapping: () => void
    onEditMapping: (mapping: CatalogFieldMapping, mappingIndex: number) => void
    onDeleteMapping: (mappingIndex: number) => void
    onAddItemIdStrategy: () => void
    onMoveItemIdStrategy: (urlIndex: number, direction: 'up' | 'down') => void
    onDeleteItemIdStrategy: (urlIndex: number) => void
    onChangeItemIdStrategy: (urlIndex: number, displayStrategy: string) => void
    onChangeItemIdField: (
        urlIndex: number,
        field: keyof ILocalItemIdConfig,
        value: string | number | undefined,
    ) => void
}

function CatalogConfigSection(props: IProps) {
    const { config, configIndex, isExpanded } = props
    const label = config.item_type ? titleCase(config.item_type) : 'New Configuration'

    return (
        <Well
            className={`type-3 nested catalog-config-section${isExpanded ? '' : ' collapsed'}`}
            footerMode="hidden"
            title={
                <Flex
                    align="center"
                    gap={8}
                    className="catalog-config-section-title"
                    onClick={props.onToggleExpanded}
                >
                    <CaretRightOutlined
                        rotate={isExpanded ? 90 : 0}
                        style={{ transition: 'transform 0.24s' }}
                    />
                    <span className={config.enabled ? '' : 'muted'}>{label}</span>
                </Flex>
            }
            actions={
                <Flex align="center" gap={8} onClick={(ev) => ev.stopPropagation()}>
                    <span className="muted small">Enabled:</span>
                    <Switch
                        checked={config.enabled}
                        onChange={(checked) => props.onUpdateConfig({ enabled: checked })}
                        size="small"
                    />
                    <Button size="small" icon={<DeleteOutlined />} onClick={props.onRemoveConfig} />
                </Flex>
            }
        >
            {isExpanded && (
                <>
                    <SchemaSettings
                        config={config}
                        availableItemTypes={props.availableItemTypes}
                        onChangeItemType={props.onChangeItemType}
                        onUpdateConfig={props.onUpdateConfig}
                    />

                    <Well
                        title={
                            <SectionTitle
                                label="Item ID Resolution"
                                tip="Define one or more strategies for extracting a unique item ID from the page. Strategies are tried in order from top to bottom — the first one that returns a value wins."
                            />
                        }
                        mode="ghost"
                        footerMode="hidden"
                    >
                        <ItemIdStrategies
                            configs={config.item_id_configs}
                            disabled={!config.enabled}
                            onAdd={props.onAddItemIdStrategy}
                            onMove={props.onMoveItemIdStrategy}
                            onDelete={props.onDeleteItemIdStrategy}
                            onChangeStrategy={props.onChangeItemIdStrategy}
                            onChangeField={props.onChangeItemIdField}
                        />
                    </Well>

                    <FieldMappings
                        config={config}
                        configIndex={configIndex}
                        canAddMapping={props.canAddMapping}
                        onAddMapping={props.onAddMapping}
                        onEditMapping={props.onEditMapping}
                        onDeleteMapping={props.onDeleteMapping}
                    />
                </>
            )}
        </Well>
    )
}

function SectionTitle(props: { label: string; tip: string }) {
    return (
        <span>
            {props.label}{' '}
            <Tooltip title={props.tip}>
                <QuestionCircleOutlined className="muted small" />
            </Tooltip>
        </span>
    )
}

function SchemaSettings(props: {
    config: ILocalCatalogConfig
    availableItemTypes: CatalogItemType[]
    onChangeItemType: (newItemType: CatalogItemType) => void
    onUpdateConfig: (patch: Partial<ILocalCatalogConfig>) => void
}) {
    const { config, availableItemTypes } = props

    return (
        <Well
            title={
                <SectionTitle
                    label="Catalog Schema Settings"
                    tip="Configure the item type and JSON-LD schema type used to identify and match catalog items on the page."
                />
            }
            mode="ghost"
            footerMode="hidden"
        >
            <Flex vertical gap={12}>
                <Flex align="center" gap={12}>
                    <span className="catalog-config-row-label">Item Type</span>
                    {config.id ? (
                        // Once persisted, item_type locks. Changing it would invalidate the
                        // saved field mappings + the parent PAC's catalog references. Delete
                        // and re-add the config to pick a different item type.
                        <Flex align="center" gap={8} className="catalog-config-locked-value">
                            <LockOutlined className="muted small" />
                            <span>{config.item_type ? titleCase(config.item_type) : '—'}</span>
                        </Flex>
                    ) : (
                        <Select
                            value={config.item_type}
                            onChange={props.onChangeItemType}
                            disabled={!config.enabled}
                            placeholder="Select item type"
                            style={{ width: 240 }}
                            options={availableItemTypes.map((t) => ({ value: t, label: titleCase(t) }))}
                        />
                    )}
                </Flex>
                <Flex align="center" gap={12}>
                    <span className="catalog-config-row-label">Schema Type</span>
                    <SchemaTypeControl config={config} onUpdateConfig={props.onUpdateConfig} />
                </Flex>
            </Flex>
        </Well>
    )
}

function SchemaTypeControl(props: {
    config: ILocalCatalogConfig
    onUpdateConfig: (patch: Partial<ILocalCatalogConfig>) => void
}) {
    const { config } = props
    const onChange = (v: string) => props.onUpdateConfig({ schema_type: v })

    if (config.item_type === CatalogItemType.TICKETED_EVENT) {
        return (
            <Select
                value={config.schema_type || 'Event'}
                onChange={onChange}
                disabled={!config.enabled}
                style={{ width: 240 }}
                options={EVENT_SCHEMA_SUBTYPES.map((t) => ({ value: t, label: t }))}
            />
        )
    }
    if (config.item_type === CatalogItemType.PRODUCT) {
        return (
            <Select
                value={config.schema_type || 'Product'}
                onChange={onChange}
                disabled={!config.enabled}
                style={{ width: 240 }}
                options={PRODUCT_SCHEMA_SUBTYPES.map((t) => ({ value: t, label: t }))}
            />
        )
    }
    return (
        <Input
            value={config.schema_type}
            disabled={true}
            placeholder="Set by item type"
            style={{ width: 240 }}
        />
    )
}

function FieldMappings(props: {
    config: ILocalCatalogConfig
    configIndex: number
    canAddMapping: boolean
    onAddMapping: () => void
    onEditMapping: (mapping: CatalogFieldMapping, mappingIndex: number) => void
    onDeleteMapping: (mappingIndex: number) => void
}) {
    const { config, configIndex } = props

    function renderActions(_text: unknown, record: CatalogFieldMapping, mappingIndex: number) {
        const isRequired = REQUIRED_CATALOG_FIELDS.includes(record.catalog_field)
        const dropdownActions: ButtonAction[] = isRequired
            ? []
            : [
                  {
                      key: 'delete',
                      text: 'Delete',
                      icon: <DeleteOutlined />,
                      onClick: () => props.onDeleteMapping(mappingIndex),
                  },
              ]
        return (
            <Button
                size="small"
                icon={<EditOutlined />}
                disabled={!config.enabled}
                onClick={() => props.onEditMapping(record, mappingIndex)}
                actions={dropdownActions.length > 0 ? dropdownActions : undefined}
            >
                Edit
            </Button>
        )
    }

    return (
        <Well
            title={
                <SectionTitle
                    label="Catalog Item Extraction Mapping"
                    tip="Map JSON-LD fields from the page to catalog item fields. Each mapping uses a JSONPath expression to extract a value from the matched JSON-LD object."
                />
            }
            mode="ghost"
            footerMode="hidden"
            actions={
                <Button
                    size="small"
                    icon={<PlusOutlined />}
                    disabled={!config.enabled || !props.canAddMapping}
                    onClick={props.onAddMapping}
                >
                    Add Mapping
                </Button>
            }
        >
            <Table<CatalogFieldMapping>
                size="small"
                pagination={false}
                dataSource={config.field_mappings}
                rowKey={(record) => `${configIndex}-fm-${record.catalog_field}`}
                rowClassName={(record) =>
                    REQUIRED_CATALOG_FIELDS.includes(record.catalog_field) ? 'required' : ''
                }
            >
                <Table.Column
                    title="Catalog Field"
                    dataIndex="catalog_field"
                    render={(value: string) => (
                        <Flex align="center" gap={6}>
                            <span>{value}</span>
                            {REQUIRED_CATALOG_FIELDS.includes(value) && (
                                <Tooltip title="Required field — value is needed for catalog matching. JSONPath can be edited; the field itself cannot be removed.">
                                    <Tag color="default" className="catalog-required-tag">
                                        Required
                                    </Tag>
                                </Tooltip>
                            )}
                        </Flex>
                    )}
                />
                <Table.Column title="JSONPath" dataIndex="json_path" />
                <Table.Column
                    title="Expected Type"
                    dataIndex="expected_type"
                    render={(value) => value || '—'}
                />
                <Table.Column title="Actions" width={180} render={renderActions} />
            </Table>
        </Well>
    )
}

export default CatalogConfigSection
